import { create } from 'zustand';
import { JobCard } from '../types';
import { 
  getJobCards, 
  getJobCardById, 
  createJobCard, 
  updateJobCard, 
  deleteJobCard as deleteJobCardAPI 
} from '../api/supabase';

interface MaintenanceState {
  jobCards: JobCard[];
  loading: boolean;
  error: string | null;
  selectedJobCard: JobCard | null;
  
  // Actions
  fetchJobCards: () => Promise<void>;
  fetchJobCardById: (id: string) => Promise<void>;
  createNewJobCard: (jobCard: Omit<JobCard, 'id' | 'created_at'>) => Promise<JobCard>;
  updateJobCard: (id: string, updates: Partial<JobCard>) => Promise<void>;
  deleteJobCard: (id: string) => Promise<void>;
  clearError: () => void;
  setSelectedJobCard: (jobCard: JobCard | null) => void;
}

export const useMaintenanceStore = create<MaintenanceState>((set, get) => ({
  jobCards: [],
  loading: false,
  error: null,
  selectedJobCard: null,

  fetchJobCards: async () => {
    set({ loading: true, error: null });
    try {
      const jobCards = await getJobCards();
      set({ jobCards, loading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch job cards',
        loading: false 
      });
    }
  },

  fetchJobCardById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const jobCard = await getJobCardById(id);
      set({ selectedJobCard: jobCard, loading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch job card',
        loading: false 
      });
    }
  },

  createNewJobCard: async (jobCardData) => {
    set({ loading: true, error: null });
    try {
      const newJobCard = await createJobCard(jobCardData as any);
      
      // Auto-update vehicle status to in_workshop when job card is created
      if (newJobCard.vehicleId) {
        try {
          const { supabase } = require('../api/supabase');
          await supabase
            .from('vehicles')
            .update({ car_status: 'in_workshop' })
            .eq('id', newJobCard.vehicleId);
          console.log('✅ Vehicle status updated to in_workshop after job card creation');
        } catch (statusError) {
          console.error('⚠️ Failed to update vehicle status:', statusError);
        }
      }
      
      const { jobCards } = get();
      set({ 
        jobCards: [newJobCard, ...jobCards],
        loading: false 
      });
      return newJobCard;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to create job card',
        loading: false 
      });
      throw error;
    }
  },

  updateJobCard: async (id: string, updates: Partial<JobCard>) => {
    set({ loading: true, error: null });
    try {
      await updateJobCard(id, updates as any);
      const { jobCards } = get();
      
      // Auto-update vehicle status if job status changed to completed/handed_over
      if (updates.jobStatus && (updates.jobStatus === 'completed' || updates.jobStatus === 'handed_over')) {
        const jobCard = jobCards.find(card => card.id === id);
        if (jobCard?.vehicleId) {
          try {
            const { supabase } = require('../api/supabase');
            await supabase
              .from('vehicles')
              .update({ car_status: 'available' })
              .eq('id', jobCard.vehicleId);
            console.log('✅ Vehicle status updated to available after job completion');
          } catch (statusError) {
            console.error('⚠️ Failed to update vehicle status:', statusError);
          }
        }
      }
      
      set({
        jobCards: jobCards.map(card => 
          card.id === id ? { ...card, ...updates } : card
        ),
        loading: false
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update job card',
        loading: false 
      });
    }
  },

  deleteJobCard: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await deleteJobCardAPI(id);
      const { jobCards } = get();
      set({
        jobCards: jobCards.filter(card => card.id !== id),
        loading: false
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to delete job card',
        loading: false 
      });
    }
  },

  clearError: () => set({ error: null }),
  setSelectedJobCard: (jobCard) => set({ selectedJobCard: jobCard }),
}));
